import React from "react";
import { useQueryClient } from "react-query";
import Reducer from "./reducer";
import { useAuth } from "shared/context/auth";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import { useGetBulkTicketPurchaseHistoryList, useInitBulkTicketPurchase } from "./dataloader";
import { BuyBulkTicketProps, BuyBulkTicketService, DispatchInterface, State } from "./types";
import { ApiPayForPurchaseOrderResponse } from "../../../../../../shared/types/ApiPayForPurchaseOrderResponse";
import ApiPurchaseOrder from "shared/model/ApiPurchaseOrder";
import PurchaseOrderStatus from "shared/enums/PurchaseOrderStatus";
import { ApiInvoicePaymentRequest } from "shared/types/ApiInvoicePaymentRequest";
import { refreshDashboardData } from "apps/myExperience/context/dashboard/dataloader";
import { updateBulkTicketDetailsInCache } from "../../dataloader";
import { shouldRefreshPageOnError } from "shared/utils/network";
import {
  getInvoicePendingPurchaseOrder,
  getOfflinePaymentApprovalPendingPurchaseOrder,
  isInvoicePaymentRequestedAndPending
} from "apps/myExperience/utils";
import { useMyExperienceDashboardDetails } from "apps/myExperience/context/dashboard";

export default function useBuyBulkTicketService(props: BuyBulkTicketProps): BuyBulkTicketService {
  const { userDetail } = useAuth();
  const queryClient = useQueryClient();
  const { data: dashboardData } = useMyExperienceDashboardDetails();
  const [state, dispatcher] = useReducerWrapper<State, DispatchInterface>(Reducer);
  const registrationParticipantId = React.useMemo(() => userDetail?.id, [userDetail?.id]);

  const { data: bulkTicketPurchaseHistory } = useGetBulkTicketPurchaseHistoryList(registrationParticipantId, data => {
    const pendingPO = data?.find(
      po =>
        po.purchaseOrderStatus === PurchaseOrderStatus.CREATED &&
        po.lines.find(line => line.originatingEntityId === props.ticketFormId)
    );
    dispatcher.updatePurchaseOrder(pendingPO);

    const invoicePaymentPendingPOForForm = getInvoicePendingPurchaseOrder(data, props.ticketFormId);
    const offlineApprovalPendingPOForForm = getOfflinePaymentApprovalPendingPurchaseOrder(data, props.ticketFormId);

    const hasAnyInvoicePaymentPendingPO = getInvoicePendingPurchaseOrder(data);
    const hasAnyOfflineApprovalPendingPO = getOfflinePaymentApprovalPendingPurchaseOrder(data);

    const isActionAvailableInDashboardData =
      dashboardData.pendingInvoicePaymentRequests?.BULK_TICKET ||
      dashboardData.pendingOfflinePaymentRequests?.BULK_TICKET;

    if (invoicePaymentPendingPOForForm || offlineApprovalPendingPOForForm) dispatcher.updateStep(2);
    else dispatcher.updateStep(1);

    if (!(hasAnyInvoicePaymentPendingPO && hasAnyOfflineApprovalPendingPO) && isActionAvailableInDashboardData)
      refreshDashboardData(queryClient, userDetail?.id);

    updateBulkTicketDetailsInCache(
      queryClient,
      registrationParticipantId ?? "",
      Boolean(invoicePaymentPendingPOForForm)
    );
  });

  const initPurchaseOrderSuccessHandler = React.useCallback(
    (data: ApiPurchaseOrder) => {
      dispatcher.updatePurchaseOrder(data);
      if (data.purchaseOrderStatus === PurchaseOrderStatus.COMPLETED) {
        dispatcher.updateStep(3);
        props.refetchList();
        const quantityPurchased = data.lines.reduce((count, lineItem) => {
          return count + lineItem.quantity;
        }, 0);
        dispatcher.updateQuantityPurchased(quantityPurchased);
      } else if (data.purchaseOrderStatus === PurchaseOrderStatus.CREATED) {
        dispatcher.updateStep(2);
      }
    },
    [props, dispatcher]
  );

  const paymentSuccessHandler = React.useCallback(
    (data: ApiPayForPurchaseOrderResponse) => {
      props.refetchList();
      const quantityPurchased = data.purchaseOrder.lines.reduce((count, lineItem) => {
        return count + lineItem.quantity;
      }, 0);
      dispatcher.updateStep(3);
      dispatcher.updatePurchaseOrder(data.purchaseOrder);
      dispatcher.updateQuantityPurchased(quantityPurchased);
      if (data.purchaseOrder.hasRequestedOfflinePayment) {
        refreshDashboardData(queryClient, userDetail?.id);
      }
    },
    [props, dispatcher, queryClient, userDetail?.id]
  );

  const offlineTransactionUpdateSuccessHandler = React.useCallback(
    (purchaseOrder: ApiPurchaseOrder) => {
      dispatcher.updateStep(3);
      const quantityPurchased = purchaseOrder.lines.reduce((count, lineItem) => {
        return count + lineItem.quantity;
      }, 0);
      props.refetchList();
      dispatcher.updateQuantityPurchased(quantityPurchased);
    },
    [props, dispatcher]
  );

  const offlineTransactionCancelSuccessHandler = React.useCallback(
    (purchaseOrder: ApiPurchaseOrder) => {
      dispatcher.updateStep(1);
      dispatcher.updatePurchaseOrder(purchaseOrder);
      refreshDashboardData(queryClient, userDetail?.id);
    },
    [dispatcher, queryClient, userDetail?.id]
  );

  const paymentErrorHandler = React.useCallback(
    async err => {
      //Payment is no longer valid then close the slider
      if (shouldRefreshPageOnError(err)) {
        props.closeHandler();
        props.refetchList();
        refreshDashboardData(queryClient, userDetail?.id);
      }
    },
    [props, queryClient, userDetail?.id]
  );

  const { mutateAsync: initBulkTicketPurchase } = useInitBulkTicketPurchase({
    successHandler: initPurchaseOrderSuccessHandler
  });

  const selectedQuantity = React.useMemo(() => {
    return state.purchaseOrder?.lines[0] ? state.purchaseOrder?.lines[0].quantity : 0;
  }, [state.purchaseOrder?.lines]);

  const isInvoicePaymentPending = React.useMemo(
    () => isInvoicePaymentRequestedAndPending(state.purchaseOrder),
    [state.purchaseOrder]
  );

  const initPurchaseHandler = React.useCallback(
    values => {
      initBulkTicketPurchase({
        registrationParticipantId: registrationParticipantId,
        ticketsPurchaseCount: values.quantity,
        ticketFormId: values.ticketFormId
      });
    },
    [initBulkTicketPurchase, registrationParticipantId]
  );

  const paymentMismatchHandler = React.useCallback(
    (obj: ApiPurchaseOrder) => {
      dispatcher.updatePurchaseOrder(obj);
    },
    [dispatcher]
  );

  const invoicePayerPaymentRequestSuccessHandler = React.useCallback(
    (data: ApiInvoicePaymentRequest) => {
      dispatcher.updateStep(3);
      const quantityPurchased = data.purchaseOrder.lines.reduce((count, lineItem) => {
        return count + lineItem.quantity;
      }, 0);
      if (state.purchaseOrder) {
        const po = {
          ...state.purchaseOrder,
          forwardedToInvoicePayer: true,
          invoicePaymentRequest: {
            invoicePayerId: data.invoicePayerId,
            invoicePayerEmail: data.invoicePayerEmail,
            invoicePaymentRequestId: data.invoicePaymentRequestId,
            status: data.status
          }
        };
        dispatcher.updatePurchaseOrder(po);
      }
      props.refetchList();
      refreshDashboardData(queryClient, registrationParticipantId);
      dispatcher.updateQuantityPurchased(quantityPurchased);
    },
    [props, dispatcher, queryClient, state.purchaseOrder, registrationParticipantId]
  );

  const resendInvoicePaymentReminderSuccessHandler = React.useCallback(() => {
    dispatcher.updateStep(3);
  }, [dispatcher]);

  return {
    initPurchaseHandler,
    registrationParticipantId,
    paymentSuccessHandler,
    state,
    dispatcher,
    selectedQuantity,
    paymentErrorHandler,
    paymentMismatchHandler,
    isInvoicePaymentPending,
    bulkTicketPurchaseHistory,
    offlineTransactionUpdateSuccessHandler,
    offlineTransactionCancelSuccessHandler,
    invoicePayerPaymentRequestSuccessHandler,
    resendInvoicePaymentReminderSuccessHandler
  };
}
